import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = ({ t }) => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.form.success);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(t.contact.form.error);
    } finally {
      setSending(false);
    }
  };

  const details = [
    { icon: MapPin, label: t.contact.addressLabel, value: t.contact.address, testid: "contact-address" },
    { icon: Mail, label: t.contact.emailLabel, value: "r.vollmermontagen@gmail.com", testid: "contact-email-info" },
    { icon: Phone, label: t.contact.phoneLabel, value: "+41 44 935 33 03 · +41 79 954 60 50", testid: "contact-phone-info" },
  ];

  return (
    <section id="kontakt" data-testid="contact-section" className="section-pad bg-[#08080a] noise-overlay scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <p data-testid="contact-overline" className="font-mono-accent text-xs uppercase tracking-[0.3em] text-pink-brand mb-4">
              {t.contact.overline}
            </p>
            <h2 data-testid="contact-title" className="font-display font-bold text-4xl md:text-5xl text-white tracking-tight">
              {t.contact.title}
            </h2>
            <p className="mt-4 max-w-lg text-base md:text-lg text-neutral-400">{t.contact.sub}</p>

            <div className="mt-12 space-y-8">
              {details.map((d) => (
                <div key={d.testid} className="flex items-start gap-4" data-testid={d.testid}>
                  <div className="w-10 h-10 border border-pink-brand flex items-center justify-center shrink-0">
                    <d.icon size={16} className="text-pink-brand" />
                  </div>
                  <div>
                    <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-neutral-500">{d.label}</p>
                    <p className="mt-1 text-white">{d.value}</p>
                  </div>
                </div>
              ))}
              <p className="font-mono-accent text-xs text-neutral-500 tracking-wider">{t.contact.officeHours}</p>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.15 }}
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="border border-white/10 p-8 md:p-10 bg-white/[0.02] space-y-6 self-start"
          >
            <div>
              <label className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-neutral-400 block mb-2">
                {t.contact.form.name}
              </label>
              <Input
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t.contact.form.namePlaceholder}
                className="rounded-none bg-transparent border-white/20 text-white placeholder:text-neutral-600 focus-visible:ring-1 focus-visible:ring-[#ff009d]"
              />
            </div>
            <div>
              <label className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-neutral-400 block mb-2">
                {t.contact.form.email}
              </label>
              <Input
                data-testid="contact-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t.contact.form.emailPlaceholder}
                className="rounded-none bg-transparent border-white/20 text-white placeholder:text-neutral-600 focus-visible:ring-1 focus-visible:ring-[#ff009d]"
              />
            </div>
            <div>
              <label className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-neutral-400 block mb-2">
                {t.contact.form.message}
              </label>
              <Textarea
                data-testid="contact-message-input"
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={t.contact.form.messagePlaceholder}
                className="rounded-none bg-transparent border-white/20 text-white placeholder:text-neutral-600 focus-visible:ring-1 focus-visible:ring-[#ff009d]"
              />
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="group w-full inline-flex items-center justify-center gap-3 bg-pink-brand text-white font-mono-accent text-xs uppercase tracking-[0.2em] px-8 py-4 hover:brightness-110 transition-[filter] duration-200 disabled:opacity-50"
            >
              {sending ? t.contact.form.sending : t.contact.form.submit}
              <Send size={14} className="transition-transform duration-200 group-hover:translate-x-1" />
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
